// Chat message bubble component with section support - 72 lines ✅
// @requirement CLAUDE.md: Show section badges for InnovationSpace messages
import { User, Bot } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import type { ConversationRole } from '@/types/database'

/**
 * Remove JSON code blocks and fenced code from agent responses.
 */
function stripJsonBlocks(text: string): string {
  let cleaned = text
  // Remove all fenced code blocks (```json...```, ```sql...```, ```csharp...```, etc.)
  cleaned = cleaned.replace(/```[\s\S]*?```/g, '')
  // Remove JSON objects containing agent metadata keys
  cleaned = cleaned.replace(/\{[^{}]*"(?:extractedData|completeness|nextAction|suggestedQuickReplies|problemTitle|problemDescription)"[^{}]*(?:\{[^{}]*\}[^{}]*)*\}/g, '')
  // Remove trailing JSON-like blocks
  cleaned = cleaned.replace(/\n\s*\{(?:\s*"[a-zA-Z]+"[\s\S]*?)\}\s*$/g, '')
  // Remove file attachment blocks
  cleaned = cleaned.replace(/---\s*ATTACHED DOCUMENTS\s*---[\s\S]*?---\s*END DOCUMENTS\s*---/g, '')
  cleaned = cleaned.replace(/\[ATTACHED IMAGE:[^\]]*\][\s\S]*?(?=\n\n|\n[A-Z]|$)/g, '')
  return cleaned.trim()
}

interface ChatMessageProps {
  role: ConversationRole
  content: string
  timestamp: string
  section?: string
  showSectionBadge?: boolean
  className?: string
}

export function ChatMessage({
  role,
  content,
  timestamp,
  section,
  showSectionBadge = false,
  className,
}: ChatMessageProps): JSX.Element {
  const isUser = role === 'user'

  // Strip JSON metadata blocks from assistant messages (agent appends structured data)
  const displayContent = isUser ? content : stripJsonBlocks(content)

  // Format section label for display
  const sectionLabel = section
    ? section
        .split('_')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
    : null

  return (
    <div
      className={cn(
        'flex gap-3 rounded-lg p-4',
        isUser ? 'bg-kaycoo-blue-pale' : 'bg-kaycoo-white-2',
        className
      )}
    >
      <div
        className={cn(
          'flex h-8 w-8 shrink-0 items-center justify-center rounded-full',
          isUser ? 'bg-kaycoo-teal-dark text-white' : 'bg-white'
        )}
      >
        {isUser ? (
          <User className="h-4 w-4" aria-hidden="true" />
        ) : (
          <Bot className="h-4 w-4" aria-hidden="true" />
        )}
      </div>
      <div className="flex-1 space-y-1">
        {/* Section Badge (for assistant messages in InnovationSpace) */}
        {showSectionBadge && !isUser && sectionLabel && (
          <div className="mb-1">
            <Badge
              variant="outline"
              className="border-kaycoo-teal-dark text-xs text-kaycoo-teal-dark"
            >
              {sectionLabel}
            </Badge>
          </div>
        )}
        <p className="text-sm leading-relaxed whitespace-pre-wrap text-kaycoo-charcoal">
          {displayContent}
        </p>
        <time className="text-xs text-kaycoo-charcoal-2">
          {new Date(timestamp).toLocaleTimeString()}
        </time>
      </div>
    </div>
  )
}
